import { catchError, EMPTY, first, lastValueFrom, map } from 'rxjs';
import { tanstackQueryService } from '@core/services/TanstackQuery.service';
import { FLOW_CHAT_ANSWER_STATUS, FLOW_CHAT_QUERY_KEYS } from '../constants/flowChat.constant';
import { flowChatService } from '../services/FlowChat.service';
import type { TGetChatHistoryRequest, TGetSessionsRequest } from '../models/flowChat.model';

class FlowChatQuery {
  public getChatSessions$ = (
    { flowId, offset, limit, order }: TGetSessionsRequest,
    enabled: boolean
  ) =>
    tanstackQueryService
      .infiniteQuery({
        queryKey: [FLOW_CHAT_QUERY_KEYS.getSessions, flowId],
        queryFn: ({ pageParam }) =>
          flowChatService.getSessions({
            flowId,
            offset: pageParam,
            limit,
            order
          }),
        enabled: enabled && !!flowId,
        initialPageParam: offset,
        getNextPageParam: (lastPage) =>
          lastPage.data.count >= lastPage.data.offset + lastPage.data.limit
            ? lastPage.data.offset + lastPage.data.limit
            : undefined
      })
      .pipe(
        map(({ data, ...rest }) => {
          if (data) {
            const sessionsList = data.pages.flatMap((page) => page.data.sessions);
            return {
              ...rest,
              data: sessionsList
            };
          }
          return { ...rest, data: [] };
        })
      );

  public getChatHistory$ = ({ flowId, sessionId, offset, limit, order }: TGetChatHistoryRequest) =>
    tanstackQueryService
      .infiniteQuery({
        queryKey: [FLOW_CHAT_QUERY_KEYS.getHistory, flowId, sessionId],
        queryFn: ({ pageParam }) =>
          flowChatService.getChatHistory({
            flowId,
            sessionId,
            offset: pageParam,
            limit,
            order
          }),
        enabled: !!sessionId && !!flowId,
        initialPageParam: offset,
        getNextPageParam: (lastPage) =>
          lastPage.data.count >= lastPage.data.offset + lastPage.data.limit
            ? lastPage.data.offset + lastPage.data.limit
            : undefined
      })
      .pipe(
        map(({ data, ...rest }) => {
          if (data) {
            const messages = data.pages.flatMap((page) => page.data.messages);
            const answerStatus = data.pages.flatMap((page) => page.data.answerStatus);

            return {
              ...rest,
              data: { messages, answerStatus: answerStatus.at(-1) }
            };
          }

          return {
            ...rest,
            data: { messages: [], answerStatus: FLOW_CHAT_ANSWER_STATUS.completed }
          };
        })
      );

  public getChatSession$ = (flowId: string, sessionId: string) =>
    tanstackQueryService
      .query({
        queryKey: [FLOW_CHAT_QUERY_KEYS.getOneSession, flowId, sessionId],
        queryFn: () => flowChatService.getSessionById({ flowId, sessionId }),
        enabled: !!sessionId
      })
      .pipe(
        map(({ data, ...rest }) => {
          if (data) {
            const session = data.data.session;
            return {
              ...rest,
              data: session
            };
          }
          return { ...rest, data: null };
        }),
        catchError(() => EMPTY)
      );

  public getShareSession = (flowId: string, sessionId: string) => {
    return lastValueFrom(
      tanstackQueryService
        .query({
          queryKey: [FLOW_CHAT_QUERY_KEYS.getShareFlowChatSession, flowId, sessionId],
          queryFn: () => flowChatService.getShareSession(flowId, sessionId)
        })
        .pipe(first(({ status }) => status !== 'pending')).observer$
    );
  };
}

const flowChatQuery = new FlowChatQuery();

export { flowChatQuery };
